import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import AnalyticsContext from 'contexts/AnalyticsContext';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import FeatureGatesContext from 'contexts/FeatureGatesContext';
import { AccountTodayOverrideContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/AccountTodayOverrideProvider';
import { V3AppsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import useCurrentAccountId from 'hooks/useCurrentAccountId';
import useCurrentInsightsEntity, {
  CurrentInsightsEntity,
} from 'hooks/useCurrentInsightsEntity';
import useInsightsChatBudget from 'hooks/useInsightsChatBudget';
import { useIsCurrentUserFleetOpsStaff } from 'components/Common/FleetOpsStaffOnly';
import useCreateInsightsContent, {
  ConfirmTarget,
} from 'components/InsightsChat/saveChart/useCreateInsightsContent';
import mergeFocusIntoView, {
  focusRowLabels,
} from 'components/InsightsChat/mergeFocusIntoView';
import mergeScopeIntoView from 'components/InsightsChat/mergeScopeIntoView';
import mergeValuesIntoView from 'components/InsightsChat/mergeValuesIntoView';
import { InsightsContentContext } from 'contextProviders/InsightsContentProvider';
import { InsightsViewFocus } from 'contextProviders/InsightsContentProvider/viewTypes';
import aguid from 'lib/aguid';
import streamInsightsQuery, {
  AnswerSource,
  InsightsQueryContext,
  ProposedAction,
} from 'services/streamInsightsQuery';
import {
  FeedbackCategory,
  FeedbackExpectation,
  FeedbackVerdict,
  submitInsightsFeedback,
} from 'services/insightsFeedback';
import captureException from 'services/captureException';
import STORE from 'store';

import { INSIGHTS_EXPLAIN_PICKER_BODY_CLASS } from '../../constants';

import categorizeInsightsError, {
  GENERIC_INSIGHTS_ERROR,
} from './categorizeInsightsError';
import {
  fromPersistedMessages,
  InsightsChatMessage,
  InsightsConversation,
  ProposedActionResult,
  toHistoryContent,
  toPersistedMessages,
} from './conversationTypes';
import detectCarryThroughFilters from './detectCarryThroughFilters';

// How many recent conversations to load for the history list.
const RECENT_CONVERSATIONS_LIMIT = 25;

// Two provenance rows describe the same query (dedupe the source footer). The
// server already dedupes per stream; this guards restored/merged message state.
const sameSource = (a: AnswerSource, b: AnswerSource): boolean =>
  a.metricId === b.metricId &&
  a.startDate === b.startDate &&
  a.endDate === b.endDate &&
  a.breakdownField === b.breakdownField &&
  a.trendByCalendarInterval === b.trendByCalendarInterval &&
  JSON.stringify(a.filters ?? null) === JSON.stringify(b.filters ?? null);

export type {
  InsightsChatRole,
  InsightsChatMessage,
} from './conversationTypes';

// The report-side "pick a value to explain" mode. While armed, the next click
// on a chart point or matrix cell is captured (see useChartDrillDowns) and sent
// to the agent as a focus instead of filtering the page.
export interface InsightsExplainPicker {
  // Whether the chat can offer the picker at all: gate + budget + an entity with
  // a live view on screen.
  isAvailable: boolean;
  isArmed: boolean;
  arm: () => void;
  disarm: () => void;
}

export interface InsightsChatContextType {
  isOpen: boolean;
  // `focus` pins the chat to one on-screen value ("Explain this value") and
  // `question` is sent as the first turn once the slideout has opened and the
  // conversation for the current entity has been resolved.
  open: (opts?: { focus?: InsightsViewFocus; question?: string }) => void;
  close: () => void;
  // The on-screen value the conversation is currently about. Persists across
  // follow-up turns; cleared by the chip, a new focus, a new chat, loading a
  // saved chat, or leaving the entity it was captured on.
  activeFocus: InsightsViewFocus | null;
  clearFocus: () => void;
  explainPicker: InsightsExplainPicker;
  messages: InsightsChatMessage[];
  isLoading: boolean;
  sendQuestion: (question: string) => void;
  // Re-send the question behind the trailing failed answer (no-op unless the
  // last message is an errored assistant turn). The failed exchange is
  // replaced by a fresh one, so the transcript never accumulates dead bubbles.
  retryLastFailed: () => void;
  // The content the user is currently viewing, scoped into questions by default
  // (null when off an entity or dismissed). dismissEntity drops it for the
  // session; it re-applies when navigating to a different entity.
  activeEntity: CurrentInsightsEntity | null;
  dismissEntity: () => void;
  // Workspace scope (optional). Narrows/improves answers via the workspace's
  // skill on the backend; auto-detected from the /workspaces/:slug route and
  // overridable via the composer dropdown. null when no workspace applies.
  selectedWorkspace: V3.Workspace | null;
  availableWorkspaces: V3.Workspace[];
  // Manual override — pass a workspace id to scope, or null to clear (which
  // re-exposes any route-detected workspace).
  setSelectedWorkspaceId: (workspaceId: string | null) => void;
  // AI spend this month (USD) from the latest usage event, null until known;
  // budgetUsd is the user's monthly cap (null = unlimited). Drives the header.
  usageSpentUsd: number | null;
  budgetUsd: number | null;
  // Start a fresh conversation; the current one stays saved in history (P5).
  startNewChat: () => void;
  // Recent saved conversations for the history list (most recent first).
  conversations: InsightsConversation[];
  // Switch the transcript to a saved conversation (no-op while a turn streams).
  loadConversation: (id: string) => void;
  // Re-fetch the recent conversations list (called when history opens).
  refreshConversations: () => void;
  // Delete a saved conversation; falls back to a fresh chat if it's the active one.
  deleteConversation: (id: string) => void;
  // Assign a custom title to a saved conversation (empty clears it).
  renameConversation: (id: string, title: string) => void;
  // Phase 3 / 3b / 3c: commit / dismiss a write the agent proposed on a message.
  // Confirm commits it (reusing the save-chart / metric-create flow) and (for
  // content) navigates to it; cancel just dismisses the card. add_to_existing
  // (3b) and add_to_workspace (3c) resolve a destination the card passes as
  // `target`; create kinds (dashboard/report/metric) ignore `target`.
  // isCreatingContent gates the confirm UI.
  confirmProposedAction: (messageId: string, target?: ConfirmTarget) => void;
  cancelProposedAction: (messageId: string) => void;
  isCreatingContent: boolean;
  // Training loop (P1): capture a reaction / correction on an assistant answer.
  // Fire-and-forget — updates the message optimistically and posts to the
  // feedback endpoint; a failure is logged, never surfaced. `correctionText` is
  // the optional note behind a 👎 (a staff note is a correction). `category` and
  // `expected` are the staff-only refinements (design §4) — the server drops them
  // for non-staff callers, so it is safe to always forward them.
  submitFeedback: (params: {
    messageId: string;
    verdict: FeedbackVerdict;
    correctionText?: string;
    category?: FeedbackCategory;
    expected?: FeedbackExpectation;
  }) => void;
  // True while a chart's Explore modal is open. The chat hides Highcharts'
  // body-level tooltip container so background dashboard tooltips don't bleed
  // over the slideout — but the Explore gadget renders its tooltips there, so
  // the suppression has to lift while it's up.
  isExploreOpen: boolean;
  setIsExploreOpen: (isOpen: boolean) => void;
}

const missingFunc = () => {
  const error = new Error();
  error.name = 'InsightsChat: Function is undefined';
  throw error;
};

export const InsightsChatContext = React.createContext<InsightsChatContextType>(
  {
    isOpen: false,
    open: missingFunc,
    close: missingFunc,
    activeFocus: null,
    clearFocus: missingFunc,
    // No-ops rather than throwing: gadget data hooks read this everywhere they
    // render (Storybook, previews) and only act on it when armed.
    explainPicker: {
      isAvailable: false,
      isArmed: false,
      arm: () => undefined,
      disarm: () => undefined,
    },
    messages: [],
    isLoading: false,
    sendQuestion: missingFunc,
    retryLastFailed: missingFunc,
    activeEntity: null,
    dismissEntity: missingFunc,
    selectedWorkspace: null,
    availableWorkspaces: [],
    setSelectedWorkspaceId: missingFunc,
    usageSpentUsd: null,
    budgetUsd: null,
    startNewChat: missingFunc,
    conversations: [],
    loadConversation: missingFunc,
    refreshConversations: missingFunc,
    deleteConversation: missingFunc,
    renameConversation: missingFunc,
    confirmProposedAction: missingFunc,
    cancelProposedAction: missingFunc,
    isCreatingContent: false,
    submitFeedback: missingFunc,
    isExploreOpen: false,
    setIsExploreOpen: missingFunc,
  },
);

// Fallback labels for the live tool-status indicator. The server now sends a
// richer, contextual `label` on each tool event (see functions describeToolStatus)
// — this map is only used when that's absent (older backend / an unseen tool).
// Keys must match the tool names the backend advertises to the model today.
const TOOL_STATUS: Record<string, string> = {
  get_domain_context: 'Getting oriented…',
  get_metric: 'Finding the right metric…',
  search_dataset: 'Searching your records…',
  list_dataset_fields: 'Checking the data schema…',
  list_field_values: 'Checking field values…',
  find_outliers: 'Finding the standouts…',
  list_topics: 'Consulting the knowledge base…',
  read_topic: 'Consulting the knowledge base…',
  get_user_usage: 'Looking at what you use most…',
  get_content_definition: 'Reading the linked report…',
  // The six KB-aligned query tools share one runner but are distinct tools.
  query_metrics: 'Crunching the numbers…',
  group_metrics: 'Breaking it down…',
  trend_metrics: 'Charting the trend…',
  trend_metrics_by_group: 'Trending each group…',
  bucket_metrics: 'Bucketing the values…',
  crosstab_metrics: 'Cross-tabulating…',
  decompose_metric: 'Breaking down the change…',
  render_chart: 'Drawing a chart…',
  propose_content: 'Preparing a build…',
  create_metric: 'Drafting a new metric…',
  create_compound_metric: 'Drafting a new metric…',
  delegate: 'Asking a specialist…',
  edit_studio_block: 'Updating the block…',
  add_studio_block: 'Adding a block…',
  move_studio_block: 'Moving the block…',
  resize_studio_block: 'Resizing the block…',
  remove_studio_block: 'Removing the block…',
  edit_studio_element: 'Updating the element…',
  add_studio_element: 'Adding an element…',
  remove_studio_element: 'Removing the element…',
  move_studio_element: 'Moving the element…',
  move_studio_row: 'Moving the row…',
  // Emitted after a tool round while the model reasons over the results.
  analyzing: 'Analyzing the results…',
};

const InsightsChatProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const accountId = useCurrentAccountId();
  const { api } = useContext(CloudFunctionClientContext);
  const { id: userId } = useContext(CurrentUserContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { budgetUsd, isEligible } = useInsightsChatBudget();
  const { isAiInsightsChatEnabled } = useContext(FeatureGatesContext);
  // Reasoning trace is FleetOps-staff-only (and off while impersonating). Drives
  // the `debug` flag on the request; the server independently re-verifies staff.
  const isFleetOpsStaff = useIsCurrentUserFleetOpsStaff();
  const { todayOverride } = useContext(AccountTodayOverrideContext);
  const {
    createContent,
    addToExisting,
    addToWorkspace,
    createMetricFromProposal,
    isSaving: isCreatingContent,
  } = useCreateInsightsContent();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  // The on-screen value the conversation is about (see the interface comment).
  const [activeFocus, setActiveFocus] = useState<InsightsViewFocus | null>(
    null,
  );
  // A question handed to `open` — sent by an effect once the slideout is open
  // and the entity's conversation has been resolved, so the first turn lands in
  // the right (fresh or resumed) transcript rather than the one being replaced.
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [isPickerArmed, setIsPickerArmed] = useState<boolean>(false);
  // Set by a chart's Explore modal so the chat can lift its Highcharts tooltip
  // suppression while that modal is up (see the interface comment).
  const [isExploreOpen, setIsExploreOpen] = useState<boolean>(false);
  const [messages, setMessages] = useState<InsightsChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [usageSpentUsd, setUsageSpentUsd] = useState<number | null>(null);
  const [dismissedEntityId, setDismissedEntityId] = useState<string | null>(
    null,
  );
  // Recent saved conversations for the history list (most recent first).
  const [conversations, setConversations] = useState<InsightsConversation[]>(
    [],
  );
  // The active conversation (P5). Refs (not state) so the persist effect reads
  // the latest without re-subscribing; null until the first message of a fresh
  // chat or a restored conversation.
  const conversationIdRef = useRef<string | null>(null);
  const conversationCreatedOnRef = useRef<string | null>(null);
  const hasLoadedRef = useRef<boolean>(false);
  // Set when messages are populated from a saved conversation (restore/load) so
  // the persist effect skips one run — viewing a chat must not re-stamp its
  // updatedOn and reorder the list.
  const skipPersistRef = useRef<boolean>(false);
  // The account the in-memory conversation belongs to. The provider is mounted
  // app-wide and survives account switches, so the persist effect (which has
  // accountId in its deps) would otherwise re-fire on a switch and write the
  // loaded chat into the NEW account — copying it into every account visited and
  // re-stamping its updatedOn to "now". This guard makes that write a no-op.
  const conversationAccountRef = useRef<string | null>(null);
  // The current account:user scope, so we can reset the in-memory chat and
  // reload the history list when the user switches account.
  const scopeRef = useRef<string | null>(null);

  const refreshConversations = useCallback(() => {
    if (!accountId || !userId) {
      return;
    }
    STORE.getInsightsConversationsRef({ accountId, userId })
      .orderBy('updatedOn', 'desc')
      .limit(RECENT_CONVERSATIONS_LIMIT)
      .get()
      .then((snapshot) => {
        setConversations(snapshot.docs.map((doc) => doc.data()));
      })
      .catch(() => {
        // Non-fatal — the history list just stays as-is.
      });
  }, [accountId, userId]);

  // Reset per-account chat state when the account (or user) changes. The
  // provider is mounted app-wide and survives account switches, so without this
  // the previous account's conversation and history list linger in memory (and
  // the persist effect would write the loaded chat into the new account).
  //
  // Defined BEFORE the restore and persist effects so its synchronous ref resets
  // are seen by them in the same commit: nulling conversationIdRef makes the
  // persist effect bail, and clearing hasLoadedRef lets the restore effect
  // reload the new account's history.
  useEffect(() => {
    if (!accountId || !userId) {
      return;
    }
    const scope = `${accountId}:${userId}`;
    if (scopeRef.current === scope) {
      return;
    }
    const isSwitch = scopeRef.current !== null;
    scopeRef.current = scope;
    if (!isSwitch) {
      // First scope seen — the restore effect handles the initial load.
      return;
    }
    conversationIdRef.current = null;
    conversationCreatedOnRef.current = null;
    conversationAccountRef.current = null;
    skipPersistRef.current = false;
    hasLoadedRef.current = false;
    setConversations([]);
    setMessages([]);
  }, [accountId, userId]);

  // First time the chat is opened: load the recent list and restore the most
  // recent conversation (unless the user already started a fresh one).
  useEffect(() => {
    if (!isOpen || hasLoadedRef.current || !accountId || !userId) {
      return undefined;
    }
    hasLoadedRef.current = true;
    let isActive = true;
    STORE.getInsightsConversationsRef({ accountId, userId })
      .orderBy('updatedOn', 'desc')
      .limit(RECENT_CONVERSATIONS_LIMIT)
      .get()
      .then((snapshot) => {
        if (!isActive || snapshot.empty) {
          return;
        }
        const recent = snapshot.docs.map((doc) => doc.data());
        setConversations(recent);
        // `open` starts a fresh chat when the slideout is opened on content
        // without a session chat; don't clobber it by restoring the last one.
        if (!suppressInitialRestoreRef.current && !conversationIdRef.current) {
          conversationIdRef.current = recent[0].id;
          conversationCreatedOnRef.current = recent[0].createdOn;
          conversationAccountRef.current = accountId;
          skipPersistRef.current = true;
          setMessages(fromPersistedMessages(recent[0].messages));
        }
        suppressInitialRestoreRef.current = false;
      })
      .catch(() => {
        // Non-fatal — start with an empty chat if history can't load.
      });
    return () => {
      isActive = false;
    };
  }, [isOpen, accountId, userId]);

  // Persist the conversation once a turn settles (isLoading false). Fire-and-
  // forget; metering/answering never blocks on it. Also upserts the in-memory
  // list so the history view stays current without a re-fetch.
  useEffect(() => {
    const conversationId = conversationIdRef.current;
    if (isLoading || messages.length === 0 || !conversationId) {
      return;
    }
    if (skipPersistRef.current) {
      // Messages came from restoring/loading a saved chat — nothing new to save.
      skipPersistRef.current = false;
      return;
    }
    if (!accountId || !userId) {
      return;
    }
    // Never write the loaded conversation into an account it doesn't belong to.
    // Order-independent guard against the account-switch re-fire of this effect
    // (the reset effect also nulls conversationIdRef, but this is the explicit
    // safety net that doesn't rely on effect ordering).
    if (
      conversationAccountRef.current &&
      conversationAccountRef.current !== accountId
    ) {
      return;
    }
    // Remember this chat against the content in view, so reopening the slideout
    // on the same report/dashboard resumes it this session.
    const entity = currentEntityRef.current;
    if (entity) {
      sessionEntityChatsRef.current.set(entity.entityId, conversationId);
    }
    const now = new Date().toISOString();
    const persisted: InsightsConversation = {
      id: conversationId,
      messages: toPersistedMessages(messages),
      createdOn: conversationCreatedOnRef.current ?? now,
      updatedOn: now,
    };
    STORE.getInsightsConversationsRef({ accountId, userId })
      .doc(conversationId)
      .set(persisted, { merge: true })
      .catch(() => {
        // Non-fatal.
      });
    setConversations((prev) => {
      // Preserve any custom title — the Firestore write is a merge, so it keeps
      // customTitle server-side; carry it in local state too.
      const existing = prev.find((c) => c.id === conversationId);
      const upserted = existing?.customTitle
        ? { ...persisted, customTitle: existing.customTitle }
        : persisted;
      return [upserted, ...prev.filter((c) => c.id !== conversationId)];
    });
  }, [isLoading, messages, accountId, userId]);

  const startNewChat = useCallback(() => {
    conversationIdRef.current = null;
    conversationCreatedOnRef.current = null;
    conversationAccountRef.current = null;
    setMessages([]);
    // A fresh chat is about nothing in particular yet.
    setActiveFocus(null);
  }, []);

  const loadConversation = useCallback(
    (id: string) => {
      // Don't swap the transcript out from under a streaming answer.
      if (isLoading) {
        return;
      }
      const conversation = conversations.find((c) => c.id === id);
      if (!conversation) {
        return;
      }
      conversationIdRef.current = conversation.id;
      conversationCreatedOnRef.current = conversation.createdOn;
      conversationAccountRef.current = accountId;
      skipPersistRef.current = true;
      setMessages(fromPersistedMessages(conversation.messages));
      // A saved conversation carries no focus of its own.
      setActiveFocus(null);
    },
    [accountId, conversations, isLoading],
  );

  const deleteConversation = useCallback(
    (id: string) => {
      if (!accountId || !userId) {
        return;
      }
      STORE.getInsightsConversationsRef({ accountId, userId })
        .doc(id)
        .delete()
        .catch(() => {
          // Non-fatal.
        });
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (conversationIdRef.current === id) {
        startNewChat();
      }
    },
    [accountId, userId, startNewChat],
  );

  const renameConversation = useCallback(
    (id: string, title: string) => {
      if (!accountId || !userId) {
        return;
      }
      const customTitle = title.trim();
      // Merge-write just the title so the rest of the conversation is untouched.
      STORE.getInsightsConversationsRef({ accountId, userId })
        .doc(id)
        .update({ customTitle })
        .catch(() => {
          // Non-fatal — the title just isn't saved.
        });
      setConversations((prev) =>
        prev.map((c) => (c.id === id ? { ...c, customTitle } : c)),
      );
    },
    [accountId, userId],
  );

  // Read imperatively at send time only — see mergeValuesIntoView /
  // mergeScopeIntoView.
  const { getViewValues, getGadgetScopes, applyFilters, getStudioBuilder } =
    useContext(InsightsContentContext);

  const setProposalStatus = useCallback(
    (
      messageId: string,
      status: 'created' | 'cancelled',
      result?: ProposedActionResult,
    ) =>
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId && m.proposedActionStatus === 'pending'
            ? {
                ...m,
                proposedActionStatus: status,
                ...(result ? { proposedActionResult: result } : {}),
              }
            : m,
        ),
      ),
    [],
  );

  // In Studio, a confirmed metric's block goes on the canvas straight away,
  // with the new metric first. Recorded on the message like a streamed add,
  // so it can be undone and the agent is told of it.
  const addProposedStudioBlock = useCallback(
    ({
      messageId,
      action,
      created,
    }: {
      messageId: string;
      action: ProposedAction;
      created: ProposedActionResult;
    }) => {
      const builder = getStudioBuilder();
      if (
        !builder ||
        (action.kind !== 'create_metric' &&
          action.kind !== 'create_compound_metric') ||
        !action.studioBlock
      ) {
        return;
      }
      const { summary, size, settings, placement } = action.studioBlock;
      const block = {
        editId: `edit_${action.id}`,
        blockId: `card-agent-${action.id}-${Date.now().toString(36)}`,
        summary,
        size,
        // The new metric is the block's metric
        settings: { ...settings, metricIds: [created.metricId] },
        ...(placement ? { placement } : {}),
      };
      const outcome = builder.addBlock(block, {
        knownMetricIds: [created.metricId],
        isStandalone: true,
      });
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                studioEdits: [
                  ...(m.studioEdits ?? []),
                  {
                    editId: block.editId,
                    blockId: block.blockId,
                    summary,
                    isNew: true,
                    ...outcome,
                  },
                ],
              }
            : m,
        ),
      );
    },
    [getStudioBuilder],
  );

  const confirmProposedAction = useCallback(
    (messageId: string, target?: ConfirmTarget) => {
      const message = messages.find((m) => m.id === messageId);
      const action = message?.proposedAction;
      if (
        !action ||
        message?.proposedActionStatus !== 'pending' ||
        isCreatingContent
      ) {
        return;
      }
      // Dispatch by kind: add_to_existing/add_to_workspace need a resolved
      // destination from the card; create kinds (dashboard/report/metric) build
      // from the proposal itself.
      // A metric resolves to what was made, so the agent can be told its id
      let commit: Promise<boolean | ProposedActionResult | undefined>;
      if (action.kind === 'add_to_existing') {
        commit =
          target && target.kind !== 'workspace'
            ? addToExisting({ target, charts: action.charts })
            : Promise.resolve(false);
      } else if (action.kind === 'add_to_workspace') {
        commit =
          target && target.kind === 'workspace'
            ? addToWorkspace({
                workspace: target.workspace,
                content: target.content,
                charts: action.charts,
              })
            : Promise.resolve(false);
      } else if (
        action.kind === 'create_metric' ||
        action.kind === 'create_compound_metric'
      ) {
        commit = createMetricFromProposal(action);
      } else {
        commit = createContent(action);
      }
      commit.then((outcome) => {
        if (outcome) {
          const created = typeof outcome === 'object' ? outcome : undefined;
          setProposalStatus(messageId, 'created', created);
          if (created) {
            addProposedStudioBlock({ messageId, action, created });
          }
          trackEvent('Insights - Proposal Confirmed', { kind: action.kind });
        }
      });
    },
    [
      messages,
      isCreatingContent,
      createContent,
      addToExisting,
      addToWorkspace,
      createMetricFromProposal,
      setProposalStatus,
      addProposedStudioBlock,
      trackEvent,
    ],
  );

  const cancelProposedAction = useCallback(
    (messageId: string) => {
      setProposalStatus(messageId, 'cancelled');
      trackEvent('Insights - Proposal Cancelled');
    },
    [setProposalStatus, trackEvent],
  );

  const currentEntity = useCurrentInsightsEntity();
  // The entity remains active unless the user dismissed *this* one. Navigating
  // to a different entity changes the id, so context re-applies automatically.
  const activeEntity =
    currentEntity && currentEntity.entityId !== dismissedEntityId
      ? currentEntity
      : null;

  // Per-session memory of which viewed report/dashboard already has an Insights
  // chat, keyed by entityId → conversation id (null until the first turn is
  // persisted). Lets a first-time open on a piece of content start fresh rather
  // than restoring an unrelated conversation, while reopening the same content
  // resumes its chat. Lives for the provider's lifetime (the session).
  const sessionEntityChatsRef = useRef<Map<string, string | null>>(new Map());
  // Assistant message ids whose filters we've already carried through to the
  // report, so a re-fired `done` (or any future effect-based trigger) can't
  // double-apply.
  const appliedMessageIdsRef = useRef<Set<string>>(new Set());
  // Set by `open` when it starts a fresh entity chat, so the first-open restore
  // effect doesn't immediately overwrite it with the most-recent conversation.
  const suppressInitialRestoreRef = useRef<boolean>(false);
  // Latest values read inside the stable `open` callback without stale closures.
  const currentEntityRef = useRef(currentEntity);
  currentEntityRef.current = currentEntity;
  const startNewChatRef = useRef(startNewChat);
  startNewChatRef.current = startNewChat;
  const loadConversationRef = useRef(loadConversation);
  loadConversationRef.current = loadConversation;

  // Workspace scope. The workspace lives in the URL (/workspaces/:slug), so it's
  // safe to route-parse it (unlike V3 content, which isn't in the URL). A manual
  // dropdown override takes precedence and persists intentionally until changed.
  const { availableWorkspaces } = useContext(V3AppsContext);
  const { pathname } = useLocation();
  const [manualWorkspaceId, setManualWorkspaceId] = useState<string | null>(
    null,
  );
  const detectedWorkspace = useMemo(() => {
    const slug = pathname.match(/\/workspaces\/([^/]+)/)?.[1];
    return slug
      ? availableWorkspaces.find((w) => w.urlSlug === slug) ?? null
      : null;
  }, [pathname, availableWorkspaces]);
  const selectedWorkspace = useMemo(
    () =>
      availableWorkspaces.find((w) => w.id === manualWorkspaceId) ??
      detectedWorkspace ??
      null,
    [availableWorkspaces, manualWorkspaceId, detectedWorkspace],
  );
  const setSelectedWorkspaceId = useCallback(
    (workspaceId: string | null) => setManualWorkspaceId(workspaceId),
    [],
  );

  const open = useCallback(
    (opts?: { focus?: InsightsViewFocus; question?: string }) => {
      const entity = currentEntityRef.current;
      if (entity) {
        const existingId =
          sessionEntityChatsRef.current.get(entity.entityId) ?? null;
        if (existingId) {
          // This report/dashboard already has a chat this session — resume it.
          if (existingId !== conversationIdRef.current) {
            loadConversationRef.current(existingId);
          }
        } else {
          // No chat for this content yet this session — start fresh instead of
          // restoring an unrelated conversation.
          sessionEntityChatsRef.current.set(entity.entityId, null);
          startNewChatRef.current();
          suppressInitialRestoreRef.current = true;
        }
      }
      // After the conversation switch above (which clears any focus), so a new
      // focus replaces the old one rather than being wiped by it.
      if (opts?.focus) {
        setActiveFocus(opts.focus);
      }
      if (opts?.question) {
        setPendingQuestion(opts.question);
      }
      setIsOpen(true);
    },
    [],
  );
  const close = useCallback(() => setIsOpen(false), []);
  const clearFocus = useCallback(() => setActiveFocus(null), []);

  // A focus belongs to the entity it was captured on. Leaving that entity (or
  // landing on a different one) drops it, so a value picked on one dashboard is
  // never attached to another's view.
  const currentEntityId = currentEntity?.entityId ?? null;
  useEffect(() => {
    setActiveFocus((prev) =>
      prev && prev.entityId !== undefined && prev.entityId !== currentEntityId
        ? null
        : prev,
    );
  }, [currentEntityId]);

  // The picker: armed from the chat composer, consumed by the next chart/cell
  // click (useExplainValue disarms it), or cancelled by Esc / navigating away.
  const armPicker = useCallback(() => setIsPickerArmed(true), []);
  const disarmPicker = useCallback(() => setIsPickerArmed(false), []);
  useEffect(() => {
    setIsPickerArmed(false);
  }, [currentEntityId, pathname]);
  useEffect(() => {
    if (!isPickerArmed) {
      return undefined;
    }
    document.body.classList.add(INSIGHTS_EXPLAIN_PICKER_BODY_CLASS);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsPickerArmed(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove(INSIGHTS_EXPLAIN_PICKER_BODY_CLASS);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isPickerArmed]);
  const explainPicker = useMemo(
    (): InsightsExplainPicker => ({
      isAvailable:
        !!isAiInsightsChatEnabled && isEligible && !!activeEntity?.view,
      isArmed: isPickerArmed,
      arm: armPicker,
      disarm: disarmPicker,
    }),
    [
      isAiInsightsChatEnabled,
      isEligible,
      activeEntity,
      isPickerArmed,
      armPicker,
      disarmPicker,
    ],
  );
  const dismissEntity = useCallback(
    () => setDismissedEntityId(currentEntity?.entityId ?? null),
    [currentEntity],
  );

  const sendQuestion = useCallback(
    (question: string, opts?: { isRetry?: boolean }) => {
      const trimmed = question.trim();
      if (!trimmed || isLoading) {
        return;
      }

      // A retry re-sends the question behind the trailing failed exchange: the
      // failed user+assistant pair is dropped and replaced with a fresh one, so
      // history/transcript never carry the dead turn. Callers guarantee the
      // last two messages ARE that pair (see retryLastFailed).
      const baseMessages = opts?.isRetry ? messages.slice(0, -2) : messages;

      // First message of a fresh chat → start a new persisted conversation,
      // tagged with the account it belongs to.
      if (!conversationIdRef.current) {
        conversationIdRef.current = aguid();
        conversationCreatedOnRef.current = new Date().toISOString();
        conversationAccountRef.current = accountId;
      }

      trackEvent('Insights - Question Asked', {
        hasEntity: activeEntity ? 'true' : 'false',
        hasFocus: activeFocus ? 'true' : 'false',
      });

      const userMessage: InsightsChatMessage = {
        id: aguid(),
        role: 'user',
        content: trimmed,
      };
      const pendingId = aguid();
      const pendingMessage: InsightsChatMessage = {
        id: pendingId,
        role: 'assistant',
        content: '',
        isLoading: true,
      };

      // Only completed turns become history sent to the agent. Assistant turns
      // get a grounding marker re-attached (toHistoryContent) so a later turn
      // can see earlier figures WERE tool-grounded — the tool_use/result blocks
      // are otherwise stripped, and the model has confabulated that it made
      // those numbers up.
      const history = baseMessages
        .filter((m) => !m.isLoading && !m.isError)
        .map((m) => ({ role: m.role, content: toHistoryContent(m) }));

      // Only the latest answer shows follow-ups: drop suggestions from earlier
      // messages as the new turn is added.
      setMessages((prev) => [
        ...(opts?.isRetry ? prev.slice(0, -2) : prev).map((m) =>
          m.suggestions ? { ...m, suggestions: undefined } : m,
        ),
        userMessage,
        pendingMessage,
      ]);
      setIsLoading(true);

      const updatePending = (
        updater: (message: InsightsChatMessage) => InsightsChatMessage,
      ) =>
        setMessages((prev) =>
          prev.map((m) => (m.id === pendingId ? updater(m) : m)),
        );

      // Workspace scope (outer) and the on-screen entity (inner narrowing)
      // coexist and are sent independently. Omit the context entirely when
      // neither applies, preserving the current no-scope behaviour.
      const context: InsightsQueryContext = {};
      if (selectedWorkspace) {
        context.workspaceId = selectedWorkspace.id;
        context.workspaceName = selectedWorkspace.label;
      }
      if (activeEntity) {
        context.entityType = activeEntity.entityType;
        // Sent for account-knowledge triggers ("when they're looking at THIS
        // report…"); the model still reads the name.
        context.entityId = activeEntity.entityId;
        context.entityName = activeEntity.entityName;
        context.metricNames = activeEntity.metricNames;
        if (activeEntity.view) {
          // Pull the on-screen scope and figures now, at send time — both live in
          // refs so that gadget refetches don't re-render the app, which means this
          // is the first and only moment they're read. Scope first: it fills each
          // gadget's ownScope while the cardId join key is still present, before
          // mergeValuesIntoView strips it.
          const withScope = mergeScopeIntoView({
            view: activeEntity.view,
            scopesByCardId: getGadgetScopes(activeEntity.entityId),
          });
          // A focus captured on THIS entity pins its row through digest sampling
          // and holds a values slot for its gadget; one from elsewhere is
          // dropped by mergeFocusIntoView.
          const focus =
            activeFocus && activeFocus.entityId === activeEntity.entityId
              ? activeFocus
              : null;
          const valuesByCardId = getViewValues(
            activeEntity.entityId,
            focus?.cardId
              ? {
                  pinned: {
                    cardId: focus.cardId,
                    labels: focusRowLabels(focus),
                  },
                }
              : undefined,
          );
          const withValues = mergeValuesIntoView({
            view: withScope,
            valuesByCardId,
            priorityCardId: focus?.cardId,
          });
          context.view = mergeFocusIntoView({
            view: withValues,
            focus,
            entityId: activeEntity.entityId,
            valuesByCardId,
          });
        }
      }
      // In the Studio builder: the canvas as it is right now, so the agent
      // can edit it (and sees the result of its last edit)
      const studioBuilder = getStudioBuilder();
      if (studioBuilder) {
        context.studio = studioBuilder.getContext();
      }
      const hasContext = Object.keys(context).length > 0;

      // Captured now, not at answer-completion: if the user navigates to another
      // report mid-answer we must still target the report the question was asked
      // about, never the newly-focused one.
      const carryThroughEntityId =
        activeEntity?.entityType === 'report' ? activeEntity.entityId : null;

      streamInsightsQuery({
        accountId,
        userId,
        budgetUsd,
        question: trimmed,
        history,
        context: hasContext ? context : undefined,
        conversationId: conversationIdRef.current ?? undefined,
        // The persisted assistant message id — stored server-side as the query
        // log's answerId so later feedback can join back to this answer.
        answerId: pendingId,
        debug: isFleetOpsStaff,
        todayOverride,
        onEvent: (event) => {
          if (event.type === 'tool') {
            // The tools the agent wrote have run, so any block placeholder
            // left was for one that never came
            if (event.name === 'analyzing') {
              getStudioBuilder()?.clearPending();
            }
            // A tool started: clear any preamble text and show its status. Also
            // record it in the reasoning trace (staff only) — except the
            // synthetic 'analyzing' status, which is a live indicator, not a
            // real tool call.
            updatePending((m) => ({
              ...m,
              content: '',
              isLoading: true,
              toolStatus: event.label ?? TOOL_STATUS[event.name] ?? 'Working…',
              trace:
                event.name === 'analyzing'
                  ? m.trace
                  : [
                      ...(m.trace ?? []),
                      {
                        kind: 'tool',
                        name: event.name,
                      },
                    ],
            }));
          } else if (event.type === 'reasoning_delta') {
            // Live thinking (staff only): grow the current reasoning entry token
            // by token. A tool entry (or the answer) segments blocks, so the
            // next delta after one starts a fresh reasoning entry — keeping the
            // trace ordered reasoning → tool(s) → reasoning → … → answer.
            updatePending((m) => {
              const trace = m.trace ?? [];
              const last = trace[trace.length - 1];
              if (last?.kind === 'reasoning') {
                return {
                  ...m,
                  trace: [
                    ...trace.slice(0, -1),
                    {
                      kind: 'reasoning',
                      text: last.text + event.text,
                    },
                  ],
                };
              }
              return {
                ...m,
                trace: [
                  ...trace,
                  {
                    kind: 'reasoning',
                    text: event.text,
                  },
                ],
              };
            });
          } else if (event.type === 'data_call') {
            // An underlying data-engine call inside the current tool (staff
            // only): append it in order so it nests under its parent tool step.
            updatePending((m) => ({
              ...m,
              trace: [
                ...(m.trace ?? []),
                {
                  kind: 'data_call',
                  name: event.name,
                  ...(event.args ? { args: event.args } : {}),
                },
              ],
            }));
          } else if (event.type === 'context_used') {
            // What the server injected before the model ran (staff only). It
            // arrives first, so it heads the trace.
            updatePending((m) => ({
              ...m,
              trace: [
                ...(m.trace ?? []),
                { kind: 'context_used', context: event.context },
              ],
            }));
          } else if (event.type === 'delta') {
            updatePending((m) => ({
              ...m,
              content: m.content + event.text,
              isLoading: false,
              toolStatus: undefined,
            }));
          } else if (event.type === 'suggestions') {
            updatePending((m) => ({
              ...m,
              suggestions: event.items,
            }));
          } else if (event.type === 'chart') {
            // Charts arrive before the terminal answer; append so multiple
            // charts in one turn accumulate. (The 'tool' branch clears content
            // but not charts, so they survive a later tool-status flip.)
            updatePending((m) => ({
              ...m,
              charts: [...(m.charts ?? []), event.spec],
            }));
            trackEvent('Insights - Chart Rendered', {
              chartType: event.spec.chartType,
            });
          } else if (event.type === 'grounding') {
            // Provenance for a number the answer uses; append (deduped) so the
            // "source" footer lists each distinct query. Like charts, it arrives
            // before the terminal answer and must survive a tool-status flip.
            updatePending((m) => {
              const sources = m.sources ?? [];
              if (sources.some((s) => sameSource(s, event.source))) {
                return m;
              }
              return { ...m, sources: [...sources, event.source] };
            });
          } else if (event.type === 'proposed_action') {
            // The agent proposed a build — render a confirm card on the message.
            updatePending((m) => ({
              ...m,
              proposedAction: event.action,
              proposedActionStatus: 'pending',
            }));
            trackEvent('Insights - Proposal Received', {
              kind: event.action.kind,
            });
          } else if (event.type === 'studio_edit') {
            // Applied now, mid-answer, so the canvas changes while the agent
            // is still talking. Outside the updater: applying sets Studio's
            // state, and updaters must stay pure. Against the builder on
            // screen now: a Studio left mid-answer applies nothing.
            const builder = getStudioBuilder();
            const outcome = builder
              ? builder.applyEdit(event.edit)
              : { isApplied: false, rejected: [] };
            updatePending((m) => ({
              ...m,
              studioEdits: [
                ...(m.studioEdits ?? []),
                {
                  editId: event.edit.editId,
                  blockId: event.edit.blockId,
                  summary: event.edit.summary,
                  ...(event.edit.element
                    ? { elementAction: event.edit.element.action }
                    : {}),
                  ...outcome,
                },
              ],
            }));
            trackEvent('Insights - Studio Edit', {
              outcome: outcome.isApplied ? 'applied' : 'not applied',
            });
          } else if (event.type === 'studio_arrange') {
            // Moved, resized or removed as it arrives, like an edit
            const builder = getStudioBuilder();
            const outcome = builder
              ? builder.arrange(event.arrange)
              : { isApplied: false, rejected: [] };
            updatePending((m) => ({
              ...m,
              studioEdits: [
                ...(m.studioEdits ?? []),
                {
                  editId: event.arrange.editId,
                  blockId: event.arrange.blockId,
                  summary: event.arrange.summary,
                  arrangeAction: event.arrange.action,
                  ...outcome,
                },
              ],
            }));
            trackEvent('Insights - Studio Edit', {
              outcome: outcome.isApplied
                ? event.arrange.action
                : `${event.arrange.action} not applied`,
            });
          } else if (event.type === 'studio_add_pending') {
            // A block the agent is writing: a placeholder until it arrives
            getStudioBuilder()?.addPending(event.pending);
          } else if (event.type === 'studio_add_cancel') {
            getStudioBuilder()?.cancelPending(event.toolUseId);
          } else if (event.type === 'studio_add') {
            // A new block, placed as it arrives, the same way as an edit
            const builder = getStudioBuilder();
            const outcome = builder
              ? builder.addBlock(event.block)
              : { isApplied: false, rejected: [] };
            updatePending((m) => ({
              ...m,
              studioEdits: [
                ...(m.studioEdits ?? []),
                {
                  editId: event.block.editId,
                  blockId: event.block.blockId,
                  summary: event.block.summary,
                  isNew: true,
                  ...outcome,
                },
              ],
            }));
            trackEvent('Insights - Studio Edit', {
              outcome: outcome.isApplied ? 'added' : 'not added',
            });
          } else if (event.type === 'usage') {
            setUsageSpentUsd(event.spentUsd);
          } else if (event.type === 'done') {
            getStudioBuilder()?.clearPending();
            updatePending((m) => {
              // Carry the agent's filter through to the report — once per answer,
              // and only when the whole answer used one consistent filter set.
              // Reading m.sources here gives the fully-accumulated, deduped set;
              // the apply is scheduled outside the reducer so the updater stays
              // pure and runs after commit.
              if (
                carryThroughEntityId &&
                !appliedMessageIdsRef.current.has(m.id)
              ) {
                const toApply = detectCarryThroughFilters(m.sources);
                if (toApply) {
                  appliedMessageIdsRef.current.add(m.id);
                  queueMicrotask(() =>
                    applyFilters(carryThroughEntityId, toApply),
                  );
                }
              }
              return { ...m, isLoading: false, toolStatus: undefined };
            });
            setIsLoading(false);
            trackEvent('Insights - Answer Completed', {
              usedTools: event.usedTools.join(',') || undefined,
            });
          } else if (event.type === 'error') {
            getStudioBuilder()?.clearPending();
            // Failed answers render as an error icon + Retry, not as a text
            // bubble. Only a SPECIFIC categorised message (timeout guidance,
            // budget, expired session) is carried for display — the generic
            // "something went wrong" line adds nothing over the icon.
            const friendly = categorizeInsightsError(event.message);
            updatePending((m) => ({
              ...m,
              content: '',
              isLoading: false,
              isError: true,
              errorMessage:
                friendly === GENERIC_INSIGHTS_ERROR ? undefined : friendly,
              toolStatus: undefined,
            }));
            setIsLoading(false);
            trackEvent('Insights - Error', {
              reason: event.reason ?? 'unknown',
            });
          }
        },
      }).catch((err) => {
        // The stream rejected before/instead of an `error` event — a network
        // failure, CORS block, or a dropped SSE connection. Historically this
        // arm discarded `err`, so the UI showed a bare "Message failed to send"
        // with nothing in the console or Sentry (see ENG-766 preview incident).
        // Surface the reason like every other gateway call: categorise it for
        // the user, and report it so it's diagnosable.
        const rawMessage = err instanceof Error ? err.message : String(err);
        const friendly = categorizeInsightsError(rawMessage);
        captureException(err instanceof Error ? err : new Error(rawMessage));
        getStudioBuilder()?.clearPending();
        updatePending((m) => ({
          ...m,
          content: '',
          isLoading: false,
          isError: true,
          errorMessage:
            friendly === GENERIC_INSIGHTS_ERROR ? undefined : friendly,
          toolStatus: undefined,
        }));
        setIsLoading(false);
        trackEvent('Insights - Error');
      });
    },
    [
      accountId,
      userId,
      budgetUsd,
      isFleetOpsStaff,
      todayOverride,
      isLoading,
      messages,
      activeEntity,
      activeFocus,
      getViewValues,
      getGadgetScopes,
      applyFilters,
      getStudioBuilder,
      selectedWorkspace,
      trackEvent,
    ],
  );

  // Send the question handed to `open` once the slideout is up and nothing is
  // streaming. Runs on the render AFTER `open`'s state updates committed, so
  // `sendQuestion` closes over the resolved transcript (fresh or resumed) and
  // the focus that was set alongside the question.
  useEffect(() => {
    if (!pendingQuestion || !isOpen || isLoading) {
      return;
    }
    setPendingQuestion(null);
    sendQuestion(pendingQuestion);
  }, [pendingQuestion, isOpen, isLoading, sendQuestion]);

  // Re-send the question behind the trailing failed answer. Guarded to exactly
  // that shape (last = errored assistant, preceded by its user question) so a
  // stale click can never re-ask an unrelated question.
  const retryLastFailed = useCallback(() => {
    const last = messages[messages.length - 1];
    const paired = messages[messages.length - 2];
    if (
      isLoading ||
      !last ||
      last.role !== 'assistant' ||
      !last.isError ||
      !paired ||
      paired.role !== 'user'
    ) {
      return;
    }
    trackEvent('Insights - Retry Clicked');
    sendQuestion(paired.content, { isRetry: true });
  }, [messages, isLoading, sendQuestion, trackEvent]);

  // Training loop (P1): record the user's reaction/correction on an answer.
  // Optimistic (the control reflects the choice immediately), then a best-effort
  // POST that never surfaces a failure to the user.
  const submitFeedback = useCallback(
    ({
      messageId,
      verdict,
      correctionText,
      category,
      expected,
    }: {
      messageId: string;
      verdict: FeedbackVerdict;
      correctionText?: string;
      category?: FeedbackCategory;
      expected?: FeedbackExpectation;
    }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, feedback: { verdict } } : m,
        ),
      );
      submitInsightsFeedback(api, {
        answerId: messageId,
        conversationId: conversationIdRef.current ?? undefined,
        verdict,
        ...(correctionText ? { correctionText } : {}),
        ...(category ? { category } : {}),
        ...(expected ? { expected } : {}),
      }).catch(() => undefined);
    },
    [api],
  );

  const value = useMemo(
    () => ({
      isOpen,
      open,
      close,
      activeFocus,
      clearFocus,
      explainPicker,
      messages,
      isLoading,
      sendQuestion,
      retryLastFailed,
      activeEntity,
      dismissEntity,
      selectedWorkspace,
      availableWorkspaces,
      setSelectedWorkspaceId,
      usageSpentUsd,
      budgetUsd,
      startNewChat,
      conversations,
      loadConversation,
      refreshConversations,
      deleteConversation,
      renameConversation,
      confirmProposedAction,
      cancelProposedAction,
      isCreatingContent,
      submitFeedback,
      isExploreOpen,
      setIsExploreOpen,
    }),
    [
      isOpen,
      open,
      close,
      activeFocus,
      clearFocus,
      explainPicker,
      messages,
      isLoading,
      sendQuestion,
      retryLastFailed,
      activeEntity,
      dismissEntity,
      selectedWorkspace,
      availableWorkspaces,
      setSelectedWorkspaceId,
      usageSpentUsd,
      budgetUsd,
      startNewChat,
      conversations,
      loadConversation,
      refreshConversations,
      deleteConversation,
      renameConversation,
      confirmProposedAction,
      cancelProposedAction,
      isCreatingContent,
      submitFeedback,
      isExploreOpen,
    ],
  );

  return (
    <InsightsChatContext.Provider value={value}>
      {children}
    </InsightsChatContext.Provider>
  );
};

export default InsightsChatProvider;
